import dayjs from 'dayjs';

const parseHourString = (str) => {
  const match = str.match(/(\d+)h\s*(\d+)m/);
  const h = match ? parseInt(match[1], 10) : 0;
  const m = match ? parseInt(match[2], 10) : 0;
  return +(h + m / 60).toFixed(2);
};

const getMaxHoursByRange = (rangeKey) => {
  if (rangeKey === 'today') return 10;
  if (rangeKey === 'week') return 50;
  if (rangeKey === 'month') return 220;
  return 12;
};

const getPaletteByRange = (rangeKey) => {
  if (rangeKey === 'today')
    return ['#F4F6F8', '#D6E4FF', '#84A9FF', '#3366FF', '#1939B7'];

  if (rangeKey === 'week')
    return ['#F4F6F8', '#C8E6E6', '#8ED1C6', '#4FB3BF', '#1F7A8C'];

  if (rangeKey === 'month')
    return ['#F4F6F8', '#FFE7BA', '#FFD591', '#FA8C16', '#AD4E00'];

  return ['#F4F6F8', '#CBD5E1', '#64748B', '#334155'];
};

const getPersonColor = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${Math.abs(hash) % 360}, 55%, 45%)`;
};

const transformHeatmapData = (rawData = []) => {
  const flat = [];
  rawData.forEach(({ date, users }) => {
    users.forEach((u) => {
      flat.push({
        date,
        name: `${u.profile.firstName} ${u.profile.lastName}`,
        hours: parseHourString(u.hours || '0h 0m')
      });
    });
  });
  return flat;
};

const TimeLoggedHeatmap = (rawData = [], rangeKey = 'today') => {
  const data = transformHeatmapData(rawData);
  const users = [...new Set(data.map((d) => d.name))];
  const dates = [...new Set(data.map((d) => d.date))].sort((a, b) =>
    dayjs(a).isAfter(dayjs(b)) ? 1 : -1
  );

  const maxHours = getMaxHoursByRange(rangeKey);
  const palette = getPaletteByRange(rangeKey);

  const avg =
    data.length > 0
      ? +(data.reduce((s, d) => s + d.hours, 0) / data.length).toFixed(2)
      : 0;

  const matrix = [];
  users.forEach((u, y) => {
    dates.forEach((d, x) => {
      const v = data.find((i) => i.name === u && i.date === d)?.hours || 0;
      matrix.push([x, y, v]);
    });
  });

  return {
    tooltip: {
      position: 'top',
      formatter: (p) => {
        if (!Array.isArray(p.value)) return '';
        const [x, y, v] = p.value;
        return `<b style="color:${getPersonColor(users[y])}">${
          users[y]
        }</b><br/>${dayjs(dates[x]).format('D MMM YYYY')}: ${v}h`;
      }
    },
    grid: {
      left: 0,
      right: 0,
      top: 40,
      bottom: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: dates.map((d) => dayjs(d).format('D MMM')),
      splitArea: { show: true }
    },
    yAxis: {
      type: 'category',
      data: users,
      axisLabel: {
        formatter: (v) => v.slice(0, 2).toUpperCase(),
        color: (v) => getPersonColor(v)
      },
      splitArea: { show: true }
    },
    visualMap: {
      show: false,
      min: 0,
      max: maxHours,
      inRange: {
        color: palette
      },
      outOfRange: {
        color: '#EEF2F6'
      }
    },
    series: [
      {
        type: 'heatmap',
        data: matrix,
        label: {
          show: true,
          formatter: (p) =>
            Array.isArray(p.value) && p.value[2] ? p.value[2] : '',
          color: (p) =>
            Array.isArray(p.value) && p.value[2] >= maxHours * 0.5
              ? '#fff'
              : '#1F2937',
          fontSize: 12
        },
        markLine: {
          symbol: 'none',
          data: [
            {
              yAxis: avg,
              lineStyle: {
                color: '#EF4444',
                width: 2,
                type: 'dashed'
              },
              label: {
                formatter: `Avg ${avg}h`,
                color: '#EF4444'
              }
            }
          ]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 8,
            shadowColor: 'rgba(0,0,0,0.25)'
          }
        }
      }
    ]
  };
};

export default TimeLoggedHeatmap;
